// import components
import WarningDialog from 'components/shared/WarningDialog/WarningDialog';
import { DrawerProps, IconButton, Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';

// import context
import { DrawerContext } from './DrawerContext';

// import external libraries
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

// import types
import {
  StyledDrawer,
  StyledDrawerContent,
  StyledDrawerTitleBox
} from './Drawer.styles';
import { DrawerContentProps, DrawerTitleProps } from './Drawer.types';

function DrawerTitle({ title, onClose }: DrawerTitleProps) {
  return (
    <StyledDrawerTitleBox>
      <Typography variant="h6" component="h2">
        {title}
      </Typography>
      <IconButton
        onClick={onClose}
        sx={{
          ml: (theme) => theme.spacing(8)
        }}
        size="large"
      >
        <Icon type="xmark" />
      </IconButton>
    </StyledDrawerTitleBox>
  );
}

function DrawerContent({ children, sx }: DrawerContentProps) {
  return <StyledDrawerContent sx={sx}>{children}</StyledDrawerContent>;
}

export type CustomDrawerProps = Omit<
  DrawerProps,
  'title' | 'children' | 'onClose' | 'sx'
> &
  DrawerTitleProps &
  DrawerContentProps;

export default function CustomDrawer({
  anchor = 'right',
  open,
  children,
  onClose,
  title,
  sx,
  ...props
}: CustomDrawerProps) {
  const { t } = useTranslation('common');
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [hasDirtyChildForm, sethasDirtyChildForm] = useState(false);

  const onCloseConfirmation = () => {
    if (hasDirtyChildForm) setShowCloseConfirmation(true);
    else {
      onClose?.();
    }
  };

  const handleClose = () => {
    setShowCloseConfirmation(false);
    sethasDirtyChildForm(false);
    onClose?.();
  };

  return (
    <DrawerContext.Provider
      value={{
        onInnerFormChange: (formState) => {
          sethasDirtyChildForm(formState.isDirty);
        }
      }}
    >
      {/* CONFIRMATION WARNING */}
      <WarningDialog
        title={t('unsavedChanges')}
        onCancel={() => setShowCloseConfirmation(false)}
        onSubmit={handleClose}
        open={showCloseConfirmation}
      />

      {/* DIALOG */}
      <StyledDrawer
        anchor={anchor}
        open={open}
        onClose={onCloseConfirmation}
        onClick={(e) => e.stopPropagation()}
        {...props}
      >
        <DrawerTitle title={title} onClose={onCloseConfirmation} />
        <DrawerContent sx={sx}>{children}</DrawerContent>
      </StyledDrawer>
    </DrawerContext.Provider>
  );
}
